import React from 'react';

import { Stack, Text } from '@mantine/core';
import { WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';

import LeaderboardRow, { LeaderboardEntry } from './LeaderboardRow';

export interface LeaderboardGroup {
  level: WorkoutLevelEnum | null;
  entries: LeaderboardEntry[];
}

interface LeaderboardBoardProps {
  groups: LeaderboardGroup[];
  highlightedAthleteId?: string | null;
  onEntryClick?: (athleteId: string) => void;
  emptyMessage?: string;
  enlarged?: boolean;
  spacious?: boolean;
}

const LeaderboardBoard = ({
  groups,
  highlightedAthleteId = null,
  onEntryClick,
  emptyMessage = 'No results yet.',
  enlarged = false,
  spacious = false,
}: LeaderboardBoardProps): JSX.Element => {
  const totalEntries = groups.reduce((sum, group) => sum + group.entries.length, 0);

  if (totalEntries === 0) {
    return (
      <Text size={enlarged ? 'md' : 'sm'} color='dimmed' align='center' py='lg'>
        {emptyMessage}
      </Text>
    );
  }

  // A single group (flat board, or one level filtered down to it) renders as one plain list —
  // level headers only earn their place when there's more than one group to tell apart.
  const showLevelHeaders = groups.length > 1;
  const groupSpacing = spacious ? 12 : enlarged ? 8 : 4;
  const rowSpacing = spacious ? 8 : enlarged ? 4 : 2;

  return (
    <Stack spacing={groupSpacing}>
      {groups.map(group => (
        <Stack key={group.level ?? 'flat'} spacing={rowSpacing}>
          {showLevelHeaders && group.level && (
            <Text size={enlarged ? 'sm' : 'xs'} weight={700} color='dimmed' transform='uppercase' mt='xs'>
              {WORKOUT_LEVEL_LABEL_MAP[group.level]}
            </Text>
          )}
          {showLevelHeaders && group.entries.length === 0 && (
            <Text size='xs' color='dimmed' py={4}>
              No scores yet
            </Text>
          )}
          {group.entries.map(entry => (
            <LeaderboardRow
              key={entry.athleteId}
              entry={entry}
              highlighted={entry.athleteId === highlightedAthleteId}
              onClick={onEntryClick}
              enlarged={enlarged}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
};

export default LeaderboardBoard;
